/**
 * Currency detection and trial pricing.
 *
 * We show prices in the visitor's currency by storing explicit per-currency
 * amounts and picking the set that matches the browser locale — there is no FX
 * conversion. This module is the single source for the currency type, symbols,
 * locale detection, amount formatting, and the Pro-trial CTA label, so the
 * pricing modal and every upgrade CTA agree on what the visitor is quoted.
 *
 * This module is intentionally duplicated from the marketing repo
 * (marketing-and-news-app src/utilities/currency.ts). Keep them in sync — a
 * visitor who sees one price on the marketing site and another here has been
 * given a reason to distrust both. Same convention as ./chargebeeCheckout.ts.
 */

export type Currency = "usd" | "gbp" | "eur";

export const CURRENCY_SYMBOLS: Record<Currency, string> = {
  usd: "$",
  gbp: "£",
  eur: "€",
};

const EUR_LANGUAGE_PREFIXES = [
  "de",
  "fr",
  "es",
  "it",
  "nl",
  "pt",
  "el",
  "fi",
  "ie",
  "at",
  "be",
  "cy",
  "ee",
  "lv",
  "lt",
  "lu",
  "mt",
  "sk",
  "si",
];

const GBP_TIMEZONES = [
  "Europe/London",
  "Europe/Belfast",
  "Europe/Guernsey",
  "Europe/Isle_of_Man",
  "Europe/Jersey",
];

const EUR_TIMEZONES_PREFIX = [
  "Europe/Berlin",
  "Europe/Paris",
  "Europe/Madrid",
  "Europe/Rome",
  "Europe/Amsterdam",
  "Europe/Lisbon",
  "Europe/Athens",
  "Europe/Helsinki",
  "Europe/Dublin",
  "Europe/Vienna",
  "Europe/Brussels",
  "Europe/Nicosia",
  "Europe/Tallinn",
  "Europe/Riga",
  "Europe/Vilnius",
  "Europe/Luxembourg",
  "Europe/Malta",
  "Europe/Bratislava",
  "Europe/Ljubljana",
];

/**
 * Best-effort detection of the visitor's currency from browser signals.
 *
 * Note this is an explicit eurozone allowlist, not a blanket `Europe/*` match —
 * a Zurich or Warsaw visitor resolves to USD, because we have no CHF or PLN
 * pricing to offer them and USD is the more sensible default of the three.
 */
export function detectCurrency(): Currency {
  if (typeof navigator === "undefined") return "usd";

  // 1. Check browser language first (most explicit signal)
  const lang = navigator.language?.toLowerCase() ?? "";
  if (lang === "en-gb" || lang === "en-uk") return "gbp";
  const prefix = lang.split("-")[0];
  if (prefix && EUR_LANGUAGE_PREFIXES.includes(prefix)) return "eur";

  // 2. Fallback to timezone detection (covers users with en-US browser language in UK/EU)
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz && GBP_TIMEZONES.includes(tz)) return "gbp";
    if (tz && EUR_TIMEZONES_PREFIX.some((eurTz) => tz === eurTz)) return "eur";
  } catch {
    // Intl not available, fall through to default
  }

  return "usd";
}

let cachedCurrency: Currency | null = null;

/**
 * The visitor's currency, resolved once per window. Nothing here changes within
 * a session, so the `Intl` lookup shouldn't re-run on every CTA render.
 *
 * Popped-out widgets are separate windows with their own module instance, so
 * they detect independently — same browser, so the same answer.
 */
export function getDetectedCurrency(): Currency {
  if (cachedCurrency === null) {
    cachedCurrency = detectCurrency();
  }
  return cachedCurrency;
}

/** Format a numeric amount with the currency symbol (e.g. 24.99 + 'gbp' → "£24.99"). */
export function formatCurrencyAmount(amount: number, currency: Currency): string {
  return `${CURRENCY_SYMBOLS[currency]}${amount.toLocaleString()}`;
}

/**
 * Price of the 7-day Pro trial, matching the Chargebee item prices. These are
 * the headline numbers only; what's actually charged is fixed by the item-price
 * IDs in PricingModal (CHARGEBEE_ITEM_PRICE_IDS.trial), so a number that drifts
 * here misquotes the visitor rather than mischarging them — which is worse, as
 * it fails silently right up until the checkout page contradicts the button.
 */
export const TRIAL_PRICES: Record<Currency, number> = {
  usd: 29.99,
  gbp: 24.99,
  eur: 29.99,
};

/**
 * Label for the demo / upgrade CTAs that open the pricing modal, resolved to
 * the visitor's currency — e.g. "Try Pro - £24.99".
 */
export function getTrialCtaLabel(): string {
  const currency = getDetectedCurrency();
  return `Try Pro - ${formatCurrencyAmount(TRIAL_PRICES[currency], currency)}`;
}
